import {ChangeEvent, memo, useCallback, useEffect, useMemo, useState} from 'react'
import {AlertExpressionDropdownProps, DropdownOption, FixedExpressionOutputTypes} from './alertExpressionDropdown.typings'
import {ChartIndicator} from '../alertModal.typings'
import {AlertExpression, AlertExpression_Type} from '../../../proto/generated/nexus'
import {Box, MenuItem, Select, SelectChangeEvent, Stack, TextField} from '@mui/material'
import {useSelector} from 'react-redux'
import {selectIndicators} from '../../../selectors/chart'
import {getSelectedValue} from '../../../util/alerts'
import {useStyles} from './styles'
import {VOLUME_INDICATOR_CODE} from '../../../assets/constants'
import {TimeBucket_TimeSeries} from '../../../proto/generated/common'
import {IndicatorOutput_OutputType} from '../../../proto/generated/indicator'
import { convertMapToKeyValueArray } from '../../../util/common'

const AlertExpressionDropdown: React.FC<AlertExpressionDropdownProps> = ({
                                                                             expression,
                                                                             chartSnapshot,
                                                                             mode,
                                                                             onExpressionChange,
                                                                             className,
                                                                             comparedExpression,
                                                                             onOutputChange
                                                                         }) => {
    const {classes} = useStyles()
    const {expression: spreadExpression, expressionMode, securityList} = chartSnapshot
    const [price , setPrice] = useState<string>(expression.fixedValue?.toString() || '0')
    const indicators = useSelector(selectIndicators)
    const indicatorMap: Map<string, ChartIndicator> = useMemo(() => {
        const map = new Map<string, ChartIndicator>()
        chartSnapshot.indicators.forEach(i => map.set(i.displayName, i))
        return map
    }, [chartSnapshot])

    useEffect(()=>{
        setPrice(expression.fixedValue?.toString() || '0')
    }, [expression?.fixedValue])

    const menuItems: DropdownOption[] = useMemo(() => {
        const options: DropdownOption[] = []
        if (mode === 'lhs') {
            options.push({
                label: 'Price',
                value: FixedExpressionOutputTypes.PRICE
            })
        } else {
            options.push({
                label: 'Value',
                value: FixedExpressionOutputTypes.VALUE
            })
        }
        if (comparedExpression) {
            const sec = comparedExpression.simpleSecurity || comparedExpression.spreadSecurity
            if (mode === 'lhs' || (!sec || sec.source !== FixedExpressionOutputTypes.VOLUME)) {
                options.push({
                    label: 'Volume',
                    value: FixedExpressionOutputTypes.VOLUME
                })
            }
        }

        chartSnapshot.indicators.forEach((ind) => {
            if (ind.code === VOLUME_INDICATOR_CODE) return
            const indicator = indicators.find(v => v.code === ind.code)
            if (!indicator?.isAlertSupported) return

            if (mode === 'rhs' && comparedExpression && comparedExpression.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
                const [id, output] = getSelectedValue(comparedExpression, indicators)
                if (id === ind.displayName && ind.outputs.length === 1) return
            }
            options.push({label: ind.displayName, value: ind.displayName})
        })
        return options
    }, [mode, comparedExpression, chartSnapshot.indicators, indicators])

    const selectedValue = useMemo(() => {
        return getSelectedValue(expression, indicators)
    }, [expression, indicators])

    const onSelect = useCallback((event: SelectChangeEvent<string>) => {
        const value = event.target.value
        let exp: AlertExpression = {
            type: expressionMode ? AlertExpression_Type.SPREAD_SECURITY : AlertExpression_Type.SIMPLE_SECURITY,
            timeBucket: {
                timeSeries: TimeBucket_TimeSeries.SECOND,
                value: 1
            },
            simpleSecurity: expressionMode ? undefined : {
                security: {code: securityList[0].code, exchange: securityList[0].exchange},
                source: 'close'
            },
            spreadSecurity: expressionMode ? {
                security: securityList.map(v => ({code: v.code, exchange: v.exchange})),
                source: 'close',
                spreadExpression
            } : undefined
        }
        switch (event.target.value) {
            case FixedExpressionOutputTypes.PRICE:
                break
            case FixedExpressionOutputTypes.VOLUME:
                if (exp.simpleSecurity) exp.simpleSecurity.source = 'volume'
                if (exp.spreadSecurity) exp.spreadSecurity.source = 'volume'
                exp.timeBucket = undefined
                break
            case FixedExpressionOutputTypes.VALUE:
                exp.type = AlertExpression_Type.FIXED_VALUE
                exp.fixedValue = 0
                exp.timeBucket = undefined
                break
            default:
                const indicator = indicatorMap.get(value)
                if (indicator) {
                    let newOutput = indicator.outputValues[selectedValue[1]] ? selectedValue[1] : indicator.outputs[0].code
                    if (mode === 'rhs' && comparedExpression && comparedExpression.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
                        const [id, output] = getSelectedValue(comparedExpression, indicators)
                        const selectedIndicator = indicatorMap.get(value)

                        if (selectedIndicator && selectedIndicator.displayName === id && selectedIndicator.outputs.length > 1) {
                            newOutput = selectedIndicator.outputs.filter(o => o.code !== output).map(o => o.code)[0] || newOutput
                        }
                    }
                    exp = {
                        type: AlertExpression_Type.TECHNICAL_INDICATOR,
                        timeBucket: undefined,
                        indicator: {
                            indicatorCode: indicator.code,
                            params: convertMapToKeyValueArray(indicator.config),
                            output: newOutput,
                            expression: {...exp, timeBucket: undefined}
                        }
                    }
                }
        }
        onExpressionChange(exp, 'type')
    }, [expressionMode, securityList, spreadExpression, onExpressionChange, indicatorMap, selectedValue, mode, comparedExpression, indicators])

    const selectedIndicator = useMemo(() => indicatorMap.get(selectedValue[0]), [indicatorMap, selectedValue])

    const showOutputDropdown = useMemo(() => {
        if (!selectedIndicator) return false
        return selectedIndicator.outputs.length > 1
    }, [selectedIndicator])

    const outputMenuItems: Array<{ key: string, label: string }> = useMemo(() => {
        if (!selectedIndicator) return []
        if (mode === 'rhs' && comparedExpression && comparedExpression.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
            const [id, output] = getSelectedValue(comparedExpression, indicators)
            if (selectedIndicator.displayName === id && selectedIndicator.outputs.length > 1) {
                return selectedIndicator.outputs
                    .filter(o =>
                        o.code !== output &&
                        o.type !== IndicatorOutput_OutputType.HZ_LINE &&
                        o.type !== IndicatorOutput_OutputType.FILL
                    )
                    .map(o => ({
                        key: o.code,
                        label: o.title
                    }))
            }
        }
        return selectedIndicator.outputs
            .filter(o => o.type !== IndicatorOutput_OutputType.HZ_LINE && o.type !== IndicatorOutput_OutputType.FILL)
            .map(o => ({key: o.code, label: o.title}))
    }, [selectedIndicator, mode, comparedExpression, indicators])

    const onOutputSelectorChange = useCallback((event: SelectChangeEvent<string>) => {
        onOutputChange(event.target.value)
    }, [onOutputChange])

    const onFixedValueChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const inputValue = event.target.value
            const newValue = inputValue.replace(/^0+(?!$)/, '')
            setPrice(newValue)
            onExpressionChange(
                {
                    ...expression,
                    fixedValue: Number(newValue)
                },
                'fixedValue'
            )
        },
        [expression, onExpressionChange]
    )

    const showValueInput = useMemo(() => expression.type === AlertExpression_Type.FIXED_VALUE && expression.fixedValue !== undefined, [expression])

    const handleBlur = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, isTriggerPrice: boolean = false) => {
        // const value = Number(e.target.value) > 0 ? Number(e.target.value) : 0
        // setPrice(value.toString())
    }, [])

    return <Box className={className}><Stack direction="row" justifyContent="space-between">

        <Select<string> className={showOutputDropdown || showValueInput ? classes.halfWidth : classes.fullWidth}
                        size="small" value={selectedValue[0]} onChange={onSelect}>
            {menuItems.map((m, i) => <MenuItem key={`${m.value}-${i}`} value={m.value}>{m.label}</MenuItem>)}
        </Select>
        {showOutputDropdown && <Select className={classes.halfWidth} size="small" value={selectedValue[1]}
                                       onChange={onOutputSelectorChange}>{outputMenuItems.map(o => <MenuItem
            key={o.key} value={o.key}>{o.label}</MenuItem>)}</Select>}
        {showValueInput && <TextField InputProps={{sx: {'& input[type=number]': { MozAppearance: 'textfield'}}}} className={classes.halfWidth} size="small" type="number" onBlur={handleBlur}  value={price} onChange={onFixedValueChange}/>}
    </Stack>
    </Box>
}

export default memo(AlertExpressionDropdown)